import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineLockClosed,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark
} from 'react-icons/hi2';
import { authService } from '../../../services';
import masterDataService from '../../../services/masterDataService';
import SharedModal from '../../../components/common/SharedModal';
import PageLoading from "../../../components/common/PageLoading";
import "../PlannedMaintenanceMasters.css";

// Synchronous cache loading function
const getCachedLockoutTagoutData = () => {
  try {
    const cachedLotoItems = sessionStorage.getItem('lockout_tagout_data');
    
    return {
      lotoItems: cachedLotoItems ? JSON.parse(cachedLotoItems) : [],
      hasAll: !!cachedLotoItems
    };
  } catch (e) {
    console.error('Error parsing cached Lockout Tagout data:', e);
    return { lotoItems: [], hasAll: false };
  }
};

const LockoutTagoutPage = () => {
  // Initialize state with cached data if available (synchronous)
  const cachedInitialData = getCachedLockoutTagoutData();
  const [lotoItems, setLotoItems] = useState(cachedInitialData.lotoItems);
  const [loading, setLoading] = useState(!cachedInitialData.hasAll);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    templateName: '',
    stepsJson: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      // Load fresh data in background if we have cached data, otherwise show loading
      const hasCachedData = cachedInitialData.hasAll;
      loadLotoItems(!hasCachedData);
    } else {
      setError('Please login to view lockout tagout templates');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const loadLotoItems = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError('');
      const data = await masterDataService.getLockoutTagouts(tenantId);
      setLotoItems(data || []);
      
      // Cache the data
      sessionStorage.setItem('lockout_tagout_data', JSON.stringify(data || []));
    } catch (err) {
      setError('Failed to load lockout tagout templates. Please try again.');
      console.error('Error loading lockout tagout templates:', err);
      // Don't clear cached data on error, keep showing it
      if (!cachedInitialData.hasAll) {
        setLotoItems([]);
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredItems = lotoItems.filter((item) =>
    item.templateName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      templateName: '',
      stepsJson: '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      templateName: item.templateName || '',
      stepsJson: typeof item.stepsJson === 'string' 
        ? item.stepsJson 
        : JSON.stringify(item.stepsJson || []),
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      // Note: deleteFn is null in config, so we'll skip delete for now
      setError('Delete functionality is not available for lockout tagout templates.');
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete lockout tagout template. Please try again.');
      console.error('Error deleting lockout tagout template:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.templateName.trim()) {
      errors.templateName = 'Template name is required';
    }
    if (formData.stepsJson) {
      try {
        JSON.parse(formData.stepsJson);
      } catch {
        errors.stepsJson = 'Invalid JSON format';
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const lotoData = {
        tenantId: tenantId,
        templateName: formData.templateName.trim(),
        stepsJson: formData.stepsJson.trim() || null,
      };

      if (editingItem) {
        lotoData.lotoId = editingItem.lotoId;
        await masterDataService.updateLockoutTagout(editingItem.lotoId, lotoData);
      } else {
        await masterDataService.createLockoutTagout(lotoData);
      }

      await loadLotoItems();
      setShowModal(false);
      setFormData({ templateName: '', stepsJson: '' });
      setEditingItem(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors || 
                          err.message || 
                          'Failed to save lockout tagout template. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({ templateName: '', stepsJson: '' });
    setFormErrors({});
  };

  if (loading) {
    return (
      <div className="pm-masters-page">
        <PageLoading message="Loading lockout tagout templates..." />
      </div>
    );
  }

  return (
    <div className="pagecontainer pm-masters-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Lockout Tagout</h1>
          <p>Manage lockout tagout templates for Planned Maintenance</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus />
          Add Template
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredItems.length} {filteredItems.length === 1 ? 'template' : 'templates'}
          </div>
        </div>

        {filteredItems.length === 0 ? (
          <div className="empty-state">
            <HiOutlineLockClosed className="empty-icon" />
            <h3>No templates found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first lockout tagout template'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                Add Template
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Template Name</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <tr key={item.lotoId}>
                  <td>{item.lotoId}</td>
                  <td className="name-cell">{item.templateName}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(item)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingItem ? 'Edit Lockout Tagout Template' : 'Add New Lockout Tagout Template'}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="loto-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting
                ? editingItem
                  ? 'Updating...'
                  : 'Creating...'
                : editingItem
                ? 'Update'
                : 'Create'}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="loto-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="templateName">
                Template Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="templateName"
                value={formData.templateName}
                onChange={(e) =>
                  setFormData({ ...formData, templateName: e.target.value })
                }
                className={formErrors.templateName ? 'error' : ''}
                placeholder="Enter template name"
              />
              {formErrors.templateName && (
                <span className="field-error">{formErrors.templateName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="stepsJson">Steps (JSON)</label>
              <textarea
                id="stepsJson"
                value={formData.stepsJson}
                onChange={(e) =>
                  setFormData({ ...formData, stepsJson: e.target.value })
                }
                placeholder='e.g., [{"step": 1, "description": "..."}] (optional)'
                rows="6"
                className={formErrors.stepsJson ? 'error' : ''}
              />
              {formErrors.stepsJson && (
                <span className="field-error">{formErrors.stepsJson}</span>
              )}
            </div>
          </form>
        </div>
      </SharedModal>
    </div>
  );
};

export default LockoutTagoutPage;
